import moment from "moment";
import numeral from "numeral";
import isEmpty from "lodash/isEmpty";
import isNull from "lodash/isNull";
import isUndefined from "lodash/isUndefined";
import React from "react";
import reduce from "lodash/reduce";
import replace from "lodash/replace";
import trim from "lodash/trim";
import isNil from "lodash/isNil";

export const MOMENT_FORMAT_SERVER = "YYYY-MM-DDTHH:mm:ss";
export const MOMENT_FORMAT_DATE_SERVER = "YYYY-MM-DD";
export const MOMENT_FORMAT_DATETIME_CLIENT = "M/D/YYYY h:mm:ss a";
export const MOMENT_FORMAT_TIME_CLIENT = "h:mm a";
export const MOMENT_FORMAT_TIME_CLIENT_WITH_SECONDS = "h:mm:ss a";
export const MOMENT_FORMAT_DATE_CLIENT_NO_SYMBOLS = "MMDDYYYY";
export const MOMENT_FORMAT_DATE_CLIENT = "M/D/YYYY";
export const MOMENT_FORMAT_DATE_TIME_CLIENT = "M/D/YYYY h:mm a";
export const MOMENT_FORMAT_DATE_CLIENT_SHORT = "M/D/YY";
export const MOMENT_FORMAT_DATE_CLIENT_DOUBLE = "MM/DD/YYYY";
export const MOMENT_FORMAT_DATE_CLIENT_PRETTY = "MMMM D, YYYY";
export const MOMENT_FORMAT_DATE_CLIENT_PRETTY_LONG = "MMMM D, YYYY, h:mm a";
export const MOMENT_FORMAT_DATE_SHORT = "MM/YYYY";
export const MOMENT_FORMAT_MONTH_YEAR = "MMMM YYYY";
export const MOMENT_FORMAT_MONTH_DAY = "MMMM D";
export const MOMENT_FORMAT_MONTH_DAY_ORDINAL = "MMMM Do";
export const MOMENT_FORMAT_MONTH_DAY_SHORT = "M/D";
export const MOMENT_FORMAT_MONTH_YEAR_SHORT = "M/YY";
export const MOMENT_FORMAT_MONTH_YEAR_ABBREVIATED = "MMM 'YY";
export const MOMENT_FORMAT_MONTH_ABBREVIATED = "MMM YYYY";
export const MOMENT_FORMAT_MONTH_ABBREVIATED_AND_DAY = "MMM D, YYYY";
export const MOMENT_FORMAT_MONTH = "MMMM";
export const MOMENT_FORMAT_YEAR = "YYYY";

export default {

    lastCharacters: (value, numCharacters) => {
        const trimmedValue = trim(value);
        return trimmedValue.substr(Math.max(0, trimmedValue.length - numCharacters));
    },

    localDateTime: (dateString) => {
        const utcDate = moment.utc(dateString, MOMENT_FORMAT_SERVER).toDate();
        return moment(utcDate).format(MOMENT_FORMAT_DATETIME_CLIENT);
    },

    localDate: (dateString) => {
        const utcDate = moment.utc(dateString, MOMENT_FORMAT_SERVER).toDate();
        return moment(utcDate).format(MOMENT_FORMAT_DATE_CLIENT);
    },

    localDateDouble: (dateString) => {
        const utcDate = moment.utc(dateString, MOMENT_FORMAT_SERVER).toDate();
        return moment(utcDate).format(MOMENT_FORMAT_DATE_CLIENT_DOUBLE);
    },

    utcDate: (dateString) => {
        return moment(dateString, MOMENT_FORMAT_SERVER).format(MOMENT_FORMAT_DATE_CLIENT);
    },

    localDateWithTime: (dateString) => {
        return moment(dateString, MOMENT_FORMAT_SERVER).format(MOMENT_FORMAT_DATE_TIME_CLIENT);
    },

    utcShortDate: (dateString) => {
        return moment(dateString, MOMENT_FORMAT_SERVER).format(MOMENT_FORMAT_DATE_SHORT);
    },

    utcPrettyDate: (dateString) => {
        return moment(dateString, MOMENT_FORMAT_SERVER).format(MOMENT_FORMAT_DATE_CLIENT_PRETTY);
    },

    utcDateDouble: (dateString) => {
        return moment(dateString, MOMENT_FORMAT_SERVER).format(MOMENT_FORMAT_DATE_CLIENT_DOUBLE);
    },

    utcMonthOnly: (dateString) => {
        return moment(dateString, MOMENT_FORMAT_SERVER).format(MOMENT_FORMAT_MONTH);
    },

    utcYearOnly: (dateString) => {
        return moment(dateString, MOMENT_FORMAT_SERVER).format(MOMENT_FORMAT_YEAR);
    },

    localTimeWithUtcTitle: (dateString) => {
        const utcDate = moment.utc(dateString, MOMENT_FORMAT_SERVER);
        return (
            <span title={utcDate.format(MOMENT_FORMAT_TIME_CLIENT) + " UTC"}>
                {moment(utcDate.toDate()).format(MOMENT_FORMAT_TIME_CLIENT)}
            </span>
        );
    },

    // Can't use an arrow function because we use "this"
    localDateTimeWithUtcTitle: function(dateString) { // eslint-disable-line object-shorthand
        return (
            <span title={moment.utc(dateString, MOMENT_FORMAT_SERVER).format(MOMENT_FORMAT_DATETIME_CLIENT) + " UTC"}>
                {this.localDateTime(dateString)}
            </span>
        );
    },

    localDateTimeStackedWithUtcTitle: function(dateString) { // eslint-disable-line object-shorthand
        const utcMoment = moment.utc(dateString, MOMENT_FORMAT_SERVER);
        const localMoment = moment(utcMoment.toDate());
        return (
            <span title={utcMoment.format(MOMENT_FORMAT_DATETIME_CLIENT) + " UTC"}>
                {localMoment.format(MOMENT_FORMAT_DATE_CLIENT)}
                <br />
                {localMoment.format(MOMENT_FORMAT_TIME_CLIENT_WITH_SECONDS)}
            </span>
        );
    },

    localPrettyDate: (dateString) => {
        const utcDate = moment.utc(dateString, MOMENT_FORMAT_SERVER).toDate();
        return moment(utcDate).format(MOMENT_FORMAT_DATE_CLIENT_PRETTY);
    },

    localPrettyDateLong: (dateString) => {
        const utcDate = moment.utc(dateString, MOMENT_FORMAT_SERVER).toDate();
        return moment(utcDate).format(MOMENT_FORMAT_DATE_CLIENT_PRETTY_LONG);
    },

    localMonthAndDay: (dateString) => {
        const utcDate = moment.utc(dateString, MOMENT_FORMAT_SERVER).toDate();
        return moment(utcDate).format(MOMENT_FORMAT_MONTH_DAY);
    },

    localMonthAndDayOrdinal: (dateString) => {
        const utcDate = moment.utc(dateString, MOMENT_FORMAT_SERVER).toDate();
        return moment(utcDate).format(MOMENT_FORMAT_MONTH_DAY_ORDINAL);
    },

    localMonthAndYear: (dateString) => {
        const utcDate = moment.utc(dateString, MOMENT_FORMAT_SERVER).toDate();
        return moment(utcDate).format(MOMENT_FORMAT_MONTH_YEAR);
    },

    localMonthAbbreviatedAndYear: (dateString) => {
        const utcDate = moment.utc(dateString, MOMENT_FORMAT_SERVER).toDate();
        return moment(utcDate).format(MOMENT_FORMAT_MONTH_ABBREVIATED);
    },

    utcMonthAndDay: (dateString) => {
        return moment(dateString, MOMENT_FORMAT_SERVER).format(MOMENT_FORMAT_MONTH_DAY);
    },

    utcMonthAndYear: (dateString) => {
        return moment(dateString, MOMENT_FORMAT_SERVER).format(MOMENT_FORMAT_MONTH_YEAR);
    },

    utcMonthAndDayShort: (dateString) => {
        return moment(dateString, MOMENT_FORMAT_SERVER).format(MOMENT_FORMAT_MONTH_DAY_SHORT);
    },

    utcMonthAndYearAbbreviated: (dateString) => {
        return moment(dateString, MOMENT_FORMAT_SERVER).format(MOMENT_FORMAT_MONTH_YEAR_ABBREVIATED);
    },

    utcMonthAbbreviatedAndYear: (dateString) => {
        return moment(dateString, MOMENT_FORMAT_SERVER).format(MOMENT_FORMAT_MONTH_ABBREVIATED);
    },

    utcMonthAbbreviatedDayAndYear: (dateString) => {
        return moment(dateString, MOMENT_FORMAT_SERVER).format(MOMENT_FORMAT_MONTH_ABBREVIATED_AND_DAY);
    },

    phoneNumber: (phone) => {
        if (!isEmpty(phone)) {
            let strippedValue = replace(phone, /[^0-9]/g, "");
            if (strippedValue.length === 10) {
                return "(" + strippedValue.substring(0, 3) + ") " + strippedValue.substring(3, 6) + "-" + strippedValue.substring(6);
            }
            if (strippedValue.length === 11) {
                return "+" + strippedValue.substring(0, 1) + "-" + strippedValue.substring(1, 4) + "-" + strippedValue.substring(4, 7) + "-" + strippedValue.substring(7);
            }
        }
        return phone;
    },

    taxId: (taxId) => {
        if (!isEmpty(taxId)) {
            let strippedValue = replace(taxId, /[^0-9]/g, "");
            if (strippedValue.length === 9) {
                return strippedValue.substring(0, 3) + "-" + strippedValue.substring(3, 5) + "-" + strippedValue.substring(5);
            }
        }
        return taxId;
    },

    maskedTaxId: (taxId) => {
        if (!isEmpty(taxId)) {
            let strippedValue = replace(taxId, /[^0-9]/g, "");
            if (strippedValue.length === 9) {
                return "XXX-XX-" + strippedValue.substring(5);
            }
        }
        return taxId;
    },

    money: (value) => {
        return numeral(value).format("$0,00.00");
    },

    moneyParenthetical: (value) => {
        return numeral(value).format("($0,00.00)");
    },

    prettyWholeNumber: (value) => {
        return numeral(value).format("0,0");
    },

    prettyNumberWithTwoDecimalPlaces: (value) => {
        return numeral(value).format("0,0.00");
    },

    prettyNumberWithDecimalsIfNecessary: (value) => {
        return numeral(value).format("0,00.[00]");
    },

    moneyWhole: (value) => {
        return numeral(value).format("$0,00");
    },

    moneyWholeFloor: (value) => {
        return numeral(Math.floor(value)).format("$0,00");
    },

    moneyWithCentsIfNecessary: (value) => {
        return numeral(value).format(value !== Math.floor(value) ? "$0,00.00" : "$0,00");
    },

    percentage: (value) => {
        return numeral(value).format("0.00%");
    },

    percentageSmall: (value) => {
        if (isNil(value)) {
            return value;
        } else {
            return numeral(value * 0.01).format("0.00%");
        }
    },

    nullable: (valueOrFunction) => {
        if (typeof valueOrFunction === "function") {
            // function
            return (v) => v ? valueOrFunction(v) : "-";
        } else {
            // value
            const useNull = isNull(valueOrFunction) || isUndefined(valueOrFunction) || valueOrFunction === "";
            return useNull ? "-" : valueOrFunction;
        }
    },

    tin: (value) => {
        if (!isEmpty(value)) {
            let strippedValue = replace(value, /[^0-9]/g, "");
            if (strippedValue.length === 9) {
                // 9 numbers and not empty is a tin, format properly
                return strippedValue.substring(0, 2) + "-" + strippedValue.substring(2);
            }
        }

        return value;
    },

    ordinal: (number) => {
        return numeral(number).format("0o");
    },

    currencyChartAxis: (value) => {
        return numeral(value).format('$0.0a');
    },

    labelledSpan: (v) => {
        return <label title={v}>{v}</label>;
    },

    numberAndNoun: (count, singularNoun, pluralNoun) => {
        return (count === 1) ? `${count} ${singularNoun}` : `${numeral(count).format("0,0")} ${pluralNoun}`;
    },

    arrayOfStringsToString: (arrayOfStrings, lastWord) => {

        const reduceFunction = (result, value, key) => {
            if (Number(key) === arrayOfStrings.length - 1) {
                result += value;
                return result;
            }
            if (Number(key) === arrayOfStrings.length - 2) {
                result += `${value} ${lastWord} `;
                return result;
            }
            result += `${value}, `;
            return result;
        };

        return reduce(arrayOfStrings, reduceFunction, "");
    }
};
